import React from "react";
import axios from "axios";
import {
  Activity,
  AlertTriangle,
  Award,
  BarChart3,
  Bell,
  BellOff,
  CalendarX,
  ChevronDown,
  ChevronUp,
  CircleHelp,
  ClipboardCheck,
  Clock,
  Cloud,
  Cpu,
  FileWarning,
  GripVertical,
  KeyRound,
  LogOut,
  Plane,
  PlaneTakeoff,
  Plus,
  Radio,
  RadioTower,
  Wrench,
  GraduationCap,
  Info,
  LineChart,
  ListTree,
  ShieldCheck,
  DollarSign,
  ExternalLink,
  FileSpreadsheet,
  Fuel,
  Mail,
  Megaphone,
  MessageSquare,
  Moon,
  Send,
  Settings,
  Sun,
  Truck,
  Download,
  UploadCloud,
  X,
} from "lucide-react";
import "./App.css";
import {
  TimezoneProvider,
  browserTimezone,
  COMMON_TIMEZONES,
  allTimezones,
} from "./time";
import { FilterDropdown } from "./components/FilterBar";
import MobileNav from "./MobileNav";
import TabletRail from "./TabletRail";
import { useCanHover, useIsPhone, useIsTablet } from "./viewport";
// Trimmed and resized from logo.png (1.94MB at 2000x1000, rendered at 36px).
import logoImg from "./logo-header.png";
import Login from "./Login";
import BackBar from "./BackBar";
import LoadingBar from "./LoadingBar";
import { pageIsAllowed } from "./pageAccess";
import { useIsDark, toggleTheme } from "./theme";

/* ============================================================
   Route-level code splitting
   ------------------------------------------------------------
   Every dashboard used to be imported statically, so opening
   Aircraft Status downloaded the mass-email editor, the
   spreadsheet exporter and all 29 other pages first -- 915 kB
   gzipped before anything appeared. Create React App printed its
   own size warning about it.

   Each page is its own chunk now. What stays in the main bundle
   is the shell: the login screen, the header, the navigation,
   the filter primitives and the time helpers -- everything
   needed before a page has been chosen.

   `load` holds the import functions rather than inlining them
   into React.lazy, because they are wanted twice: once to define
   the component and once to warm the chunk from a hover. Webpack
   caches the module promise, so a prefetch and the render that
   follows it share one request.
   ============================================================ */
const load = {
  AdminPanel: () => import("./AdminPanel"),
  KnowledgeBaseAdmin: () => import("./KnowledgeBaseAdmin"),
  TicketConsole: () => import("./TicketConsole"),
  FlightsUploadModal: () => import("./FlightsUploadModal"),
  TechEfficiencyUploadModal: () => import("./TechEfficiencyUploadModal"),
  AlertModal: () => import("./AlertModal"),
  AircraftStatus: () => import("./AircraftStatus"),
  TrainingHours: () => import("./TrainingHours"),
  FleetUtilization: () => import("./FleetUtilization"),
  SimulatorStatus: () => import("./SimulatorStatus"),
  BGSUCourseCompletion: () => import("./BGSUCourseCompletion"),
  CourseCompletion: () => import("./CourseCompletion"),
  WeatherConditions: () => import("./WeatherConditions"),
  FlightTrainingRevenue: () => import("./FlightTrainingRevenue"),
  CustomerMaintenanceRevenue: () => import("./CustomerMaintenanceRevenue"),
  FinancialStatements: () => import("./FinancialStatements"),
  OilConsumption: () => import("./OilConsumption"),
  MaintenanceThroughput: () => import("./MaintenanceThroughput"),
  TechEfficiency: () => import("./TechEfficiency"),
  FleetReliability: () => import("./FleetReliability"),
  FlightOperations: () => import("./FlightOperations"),
  FrequencyRecordings: () => import("./FrequencyRecordings"),
  Cancellations: () => import("./Cancellations"),
  SMSOverview: () => import("./SMSOverview"),
  AccidentRate: () => import("./AccidentRate"),
  SMSHFACSAnalysis: () => import("./SMSHFACSAnalysis"),
  SafetyDigestSetup: () => import("./SafetyDigestSetup"),
  SafetyCultureSurvey: () => import("./SafetyCultureSurvey"),
  AirSyncHealth: () => import("./AirSyncHealth"),
  RampOpsDashboard: () => import("./RampOpsDashboard"),
  RampActivityRecords: () => import("./RampActivityRecords"),
  EmailCampaigns: () => import("./pages/EmailCampaigns"),
  Lifecycles: () => import("./Lifecycles"),
  AlertsManager: () => import("./AlertsManager"),
  SystemStatus: () => import("./SystemStatus"),
};

const AdminPanel = React.lazy(load.AdminPanel);
const KnowledgeBaseAdmin = React.lazy(load.KnowledgeBaseAdmin);
const TicketConsole = React.lazy(load.TicketConsole);
const FlightsUploadModal = React.lazy(load.FlightsUploadModal);
const TechEfficiencyUploadModal = React.lazy(load.TechEfficiencyUploadModal);
const AlertModal = React.lazy(load.AlertModal);
const AircraftStatus = React.lazy(load.AircraftStatus);
const TrainingHours = React.lazy(load.TrainingHours);
const FleetUtilization = React.lazy(load.FleetUtilization);
const SimulatorStatus = React.lazy(load.SimulatorStatus);
const BGSUCourseCompletion = React.lazy(load.BGSUCourseCompletion);
const CourseCompletion = React.lazy(load.CourseCompletion);
const WeatherConditions = React.lazy(load.WeatherConditions);
const FlightTrainingRevenue = React.lazy(load.FlightTrainingRevenue);
const CustomerMaintenanceRevenue = React.lazy(load.CustomerMaintenanceRevenue);
const FinancialStatements = React.lazy(load.FinancialStatements);
const OilConsumption = React.lazy(load.OilConsumption);
const MaintenanceThroughput = React.lazy(load.MaintenanceThroughput);
const TechEfficiency = React.lazy(load.TechEfficiency);
const FleetReliability = React.lazy(load.FleetReliability);
const FlightOperations = React.lazy(load.FlightOperations);
const FrequencyRecordings = React.lazy(load.FrequencyRecordings);
const Cancellations = React.lazy(load.Cancellations);
const SMSOverview = React.lazy(load.SMSOverview);
const AccidentRate = React.lazy(load.AccidentRate);
const SMSHFACSAnalysis = React.lazy(load.SMSHFACSAnalysis);
const SafetyDigestSetup = React.lazy(load.SafetyDigestSetup);
const SafetyCultureSurvey = React.lazy(load.SafetyCultureSurvey);
const AirSyncHealth = React.lazy(load.AirSyncHealth);
const RampOpsDashboard = React.lazy(load.RampOpsDashboard);
const RampActivityRecords = React.lazy(load.RampActivityRecords);
const EmailCampaigns = React.lazy(load.EmailCampaigns);
const Lifecycles = React.lazy(load.Lifecycles);
const AlertsManager = React.lazy(load.AlertsManager);
const SystemStatus = React.lazy(load.SystemStatus);

/* Start fetching a page's chunk before it is asked for.

   Desktop navigation was instant when everything was already in memory, and
   splitting would have made the first visit to each page wait on the network.
   Hovering a menu item is a reliable half-second of warning, and on a touch
   screen the same call fires when the sheet opens. Failures are swallowed on
   purpose: this is a hint, and if it does not land, React.lazy will fetch the
   chunk for real a moment later. */
function prefetchPage(pageName) {
  const loader = PAGE_PREFETCH[pageName];
  if (loader) loader().catch(() => {});
}

const ADMIN_BASES = ["BGFC", "NSA", "PFC"];

const SECTIONS = {
  "FLIGHT OPERATIONS": [
    "Fleet Utilization Overview",
    "Training Hours",
    "Departure & Arrival Analysis",
    "Flight Trends",
    "Revenue - Flight Training",
    "Cancellations",
    "Frequency Recordings",
    "Aircraft Performance Profile",
    "Cancellations - Overview",
    "Landings",
    "Cancellations - Trend Detail",
  ],
  MAINTENANCE: [
    "Aircraft Status",
    "Maintenance Throughput",
    "Tech Efficiency",
    "Simulator Status",
    "Maintenance Surveillance",
    "Lifecycles",
    "Oil Consumption",
    "Expenses - Fleet MX",
    "Aircraft Down Analysis",
    "Labor Hours - Fleet vs Customer",
    "Resource Down History Duration",
  ],
  // "Course Completion" is the PAGE KEY, displayed as "Training Timelines" --
  // see PAGE_LABELS. It covers all three schools; BGSU Course Completion is the
  // BGFC-only page it replaces, kept alongside until the new one is refined.
  ACADEMICS: ["Course Completion", "BGSU Course Completion"],
  SAFETY: [
    "Weather Conditions",
    "Safety Cases",
    "Accident Rate",
    "HFACS Analysis",
    "Weekly Digest",
    "Safety Culture Survey",
    "AirSync Health",
  ],
  FINANCE: ["Flight Training Revenue", "Maintenance Billing", "Financial Statements"],
  "RAMP OPERATIONS": [
    "Ramp Operations Overview",
    "Ramp Activity Records",
  ],
};

/* Not in SECTIONS on purpose: the mass email tool, the knowledge base and the
   ticket console are working surfaces rather than dashboards, so they live in
   the account menu beside Alert Manager and Admin instead of in the dashboard
   nav and on the home screen. Everything else about them is unchanged -- they
   stay in builtPages, PAGE_COMPONENTS and PAGE_PREFETCH below and keep the
   permissions they always checked, and AdminPanel.js still groups them under
   EMAIL and SUPPORT so they remain grantable. Only the way in changed.

   As before, only EmailCampaigns carries a PAGE_TO_PATH entry; the other two
   have never had a URL of their own and are still reached by name. */

const DIRECT_NAV_ITEMS = new Set(["Alerts"]);

const builtPages = new Set([
  "Fleet Utilization Overview",
  "Training Hours",
  "Aircraft Status",
  "Tech Efficiency",
  "Simulator Status",
  "Course Completion",
  "BGSU Course Completion",
  "Weather Conditions",
  "Safety Cases",
  "Accident Rate",
  "HFACS Analysis",
  "Weekly Digest",
  "Safety Culture Survey",
  "AirSync Health",
  "Flight Training Revenue",
  "Departure & Arrival Analysis",
  "Cancellations",
  "Frequency Recordings",
  "Ramp Operations Overview",
  "Maintenance Billing",
  "Financial Statements",
  "Maintenance Surveillance",
  "Lifecycles",
  "Oil Consumption",
  "Maintenance Throughput",
  "Ramp Activity Records",
  "EmailCampaigns",
  "Alerts",
  "System Status",
  "Knowledge Base",
  "Support Tickets",
]);

const PAGE_PERMISSION_OVERRIDES = {
  // Configures who receives safety case detail by email, so it rides its own
  // capability rather than the Safety Cases page grant: reading the dashboard
  // and deciding what leaves it are different decisions.
  "Weekly Digest": "safety_cases.digest.manage",
  "Lifecycles": "lifecycles.view",
  // Names servers, endpoints and failure messages, so it rides the admin
  // capability rather than the dashboard page grants. Admins inherit it.
  "System Status": "admin.system_status.view",
  // Agent-side surfaces. Each rides its own capability rather than a page
  // grant, so authoring and ticket work can be handed out separately -- and
  // neither requires access to the admin panel any more. Admins inherit both.
  "Knowledge Base": "admin.kb.manage",
  "Support Tickets": "admin.tickets.view",
  // Comment text and the wave lifecycle hang off this one, so the page rides a
  // named permission rather than its own title. It MUST match what the admin
  // panel stores for the page -- PAGE_PERMISSION_VALUES in AdminPanel.js --
  // or the grant writes one string and this checks another, and the page is
  // invisible to everyone except admins, who never reach this line.
  "Safety Culture Survey": "safetyculture.view",
  // QuickBooks' Profit & Loss, Balance Sheet and Cash Flow -- the company's
  // books. Must match PAGE_PERMISSION_VALUES in AdminPanel.js, as above, and
  // FINANCIALS_PERMISSION in quickbooks.py. It is in EXPLICIT_GRANT_PAGES too,
  // so admins do not reach it without the grant either. The page was called
  // Profit & Loss until it grew the other two; the grant never named the page,
  // so renaming it moved no one's access.
  "Financial Statements": "quickbooks.financials",
};

/* Pages that being an admin does not open. The server checks their permission
   with admin_override=False, so the page answers 403 to an admin without the
   grant -- offering it in the nav anyway would be a menu item that cannot be
   opened, and would say the wrong thing about who can see the books. */
const EXPLICIT_GRANT_PAGES = new Set(["Financial Statements"]);

/* Grants that open a page besides its own, for a part of it given on its own.
   Term Debt is a tab of Financial Statements given apart from the books
   (Bryan, 3 November 2026): it opens the page on that tab alone. Being an
   admin opens neither (term_debt.py). */
export const TERM_DEBT_PERMISSION = "quickbooks.termdebt";
export const TERM_DEBT_UPLOAD_PERMISSION = "quickbooks.termdebt.upload";
// Borrowing Base, the same again for a different document (3 November 2026).
export const BORROWING_BASE_PERMISSION = "quickbooks.borrowingbase";
export const BORROWING_BASE_UPLOAD_PERMISSION = "quickbooks.borrowingbase.upload";
const ALSO_OPENED_BY = {
  "Financial Statements": [
    "quickbooks.termdebt", "quickbooks.termdebt.upload",
    "quickbooks.borrowingbase", "quickbooks.borrowingbase.upload",
  ],
};

const PAGE_LABELS = {
  EmailCampaigns: "Mass Email Tool",
  Alerts: "Alert Manager",
  // The page key stays "Maintenance Surveillance" because it is the value
  // stored in every user's allowed_pages; only the displayed name changed.
  "Maintenance Surveillance": "Fleet Reliability",
  // Same again. The page outgrew "Course Completion" -- it measures the span
  // between any two points in a training record, and a waiting period has no
  // completion to speak of -- but the old name is the string sitting in
  // FOX_Permissions for every user who can open it. Renaming the KEY would
  // mean a migration, and a window during a rolling deploy where the old
  // container checks one name against rows carrying the other. Renaming the
  // LABEL costs nothing and nobody outside this file sees the difference.
  "Course Completion": "Training Timelines",
};

// Which dashboard's alerts the bell on this page opens. The metric list used
// to live here too, written out a third time after AlertsManager.js and
// main.py; it comes from /api/alerts/catalog now, so adding a dashboard to
// alerting is one edit rather than three.
const PAGE_ALERT_DASHBOARD = {
  "Aircraft Status": "aircraft-status",
  "Fleet Utilization Overview": "fleet-utilization",
  "Training Hours": "training-hours",
  Cancellations: "cancellations",
  "Flight Training Revenue": "revenue-flight-training",
  "Revenue - Flight Training": "revenue-flight-training",
  "Simulator Status": "simulator-status",
  "Oil Consumption": "oil-consumption",
  "AirSync Health": "airsync",
  "Safety Cases": "safety-cases",
  "Maintenance Billing": "maintenance-billing",
  "Weather Conditions": "weather",
  "Safety Culture Survey": "safety-culture-survey",
  "System Status": "system-status",
};

const LEGACY_PAGE_PERMISSIONS = {};

const PAGE_TO_PATH = {
  Home: "/",
  Admin: "/admin",
  "Fleet Utilization Overview": "/flight-ops/fleet-utilization",
  "Training Hours": "/flight-ops/training-hours",
  "Departure & Arrival Analysis": "/flight-ops/departures-arrivals",
  Cancellations: "/flight-ops/cancellations",
  "Frequency Recordings": "/flight-ops/frequency-recordings",
  "Aircraft Status": "/maintenance/aircraft-status",
  "Tech Efficiency": "/maintenance/tech-efficiency",
  "Simulator Status": "/maintenance/simulator-status",
  "Maintenance Billing": "/finance/maintenance-billing",
  "Financial Statements": "/finance/financial-statements",
  "Maintenance Surveillance": "/maintenance/fleet-reliability",
  "Lifecycles": "/maintenance/lifecycles",
  "Oil Consumption": "/maintenance/oil-consumption",
  "Maintenance Throughput": "/maintenance/throughput",
  "Course Completion": "/academics/training-timelines",
  "BGSU Course Completion": "/academics/bgsu-course-completion",
  "Weather Conditions": "/safety/weather-conditions",
  "Safety Cases": "/safety/cases",
  "Accident Rate": "/safety/accident-rate",
  "HFACS Analysis": "/safety/hfacs-analysis",
  "Safety Culture Survey": "/safety/culture-survey",
  "AirSync Health": "/safety/airsync-health",
  "Flight Training Revenue": "/finance/flight-training-revenue",
  "Ramp Operations Overview": "/ramp-ops/overview",
  "Ramp Activity Records": "/ramp-ops/activity-records",
  EmailCampaigns: "/email-campaigns",
  Alerts: "/alerts",
  "System Status": "/system-status",
};

const PATH_TO_PAGE = {
  ...Object.fromEntries(Object.entries(PAGE_TO_PATH).map(([page, path]) => [path, page])),
  // Bookmarks and links from before the page was renamed.
  "/maintenance/surveillance": "Maintenance Surveillance",
  "/academics/course-completion": "Course Completion",
  "/finance/profit-and-loss": "Financial Statements",
};

function pageFromLocation() {
  return PATH_TO_PAGE[window.location.pathname] || "Home";
}

function pageLabel(page) {
  return PAGE_LABELS[page] || page;
}

function sectionPages(pages) {
  return Array.isArray(pages) ? pages : [pages];
}

const PAGE_COMPONENTS = {
  "Aircraft Status": AircraftStatus,
  "Tech Efficiency": TechEfficiency,
  "Training Hours": TrainingHours,
  "Fleet Utilization Overview": FleetUtilization,
  "Simulator Status": SimulatorStatus,
  "Course Completion": CourseCompletion,
  "BGSU Course Completion": BGSUCourseCompletion,
  "Weather Conditions": WeatherConditions,
  "Flight Training Revenue": FlightTrainingRevenue,
  "Maintenance Billing": CustomerMaintenanceRevenue,
  "Financial Statements": FinancialStatements,
  "Maintenance Surveillance": FleetReliability,
  "Lifecycles": Lifecycles,
  "Oil Consumption": OilConsumption,
  "Maintenance Throughput": MaintenanceThroughput,
  "Departure & Arrival Analysis": FlightOperations,
  Cancellations,
  "Frequency Recordings": FrequencyRecordings,
  "Ramp Operations Overview": RampOpsDashboard,
  "Ramp Activity Records": RampActivityRecords,
  "AirSync Health": AirSyncHealth,
  EmailCampaigns,
  Alerts: AlertsManager,
  "System Status": SystemStatus,
  "Knowledge Base": KnowledgeBaseAdmin,
  "Support Tickets": TicketConsole,
};

/* Which chunk each page lives in, for prefetching.

   It has to be written out rather than derived from PAGE_COMPONENTS above,
   because that map holds the already-wrapped lazy components and React gives
   no way back to the import function from one. The four safety pages are here
   too: renderDashboardPage reaches them by name rather than through
   PAGE_COMPONENTS, so they would otherwise be the pages that never prefetch.

   The dev-only check below fails loudly if a page is added to one and not the
   other, which is the drift this shape invites. */
const PAGE_PREFETCH = {
  "Aircraft Status": load.AircraftStatus,
  "Tech Efficiency": load.TechEfficiency,
  "Training Hours": load.TrainingHours,
  "Fleet Utilization Overview": load.FleetUtilization,
  "Simulator Status": load.SimulatorStatus,
  "Course Completion": load.CourseCompletion,
  "BGSU Course Completion": load.BGSUCourseCompletion,
  "Weather Conditions": load.WeatherConditions,
  "Flight Training Revenue": load.FlightTrainingRevenue,
  "Maintenance Billing": load.CustomerMaintenanceRevenue,
  "Financial Statements": load.FinancialStatements,
  "Maintenance Surveillance": load.FleetReliability,
  Lifecycles: load.Lifecycles,
  "Oil Consumption": load.OilConsumption,
  "Maintenance Throughput": load.MaintenanceThroughput,
  "Departure & Arrival Analysis": load.FlightOperations,
  Cancellations: load.Cancellations,
  "Frequency Recordings": load.FrequencyRecordings,
  "Ramp Operations Overview": load.RampOpsDashboard,
  "Ramp Activity Records": load.RampActivityRecords,
  "AirSync Health": load.AirSyncHealth,
  EmailCampaigns: load.EmailCampaigns,
  Alerts: load.AlertsManager,
  "System Status": load.SystemStatus,
  "Knowledge Base": load.KnowledgeBaseAdmin,
  "Support Tickets": load.TicketConsole,
  // Reached by name in renderDashboardPage, not through PAGE_COMPONENTS.
  "Safety Cases": load.SMSOverview,
  "Accident Rate": load.AccidentRate,
  "HFACS Analysis": load.SMSHFACSAnalysis,
  "Weekly Digest": load.SafetyDigestSetup,
  Admin: load.AdminPanel,
};

if (process.env.NODE_ENV !== "production") {
  const missing = Array.from(builtPages).filter((page) => !PAGE_PREFETCH[page]);
  if (missing.length) {
    // eslint-disable-next-line no-console
    console.warn(
      `PAGE_PREFETCH is missing an entry for: ${missing.join(", ")}. ` +
        "Those pages still work, they just will not warm their chunk on hover."
    );
  }
}

function renderDashboardPage(
  page,
  allowedPermissions,
  allowedBases,
  onAlertsChanged,
  onCancellationsExportReady,
  onTechEfficiencyMappingsReady,
  canManageTickets,
  onSafetyCultureSetupReady,
  onSafetyCultureTriageReady,
  onFinancialSettingsReady,
  onSafetyCultureReportReady
) {
  if (page === "Support Tickets") return <TicketConsole canManage={canManageTickets} />;
  if (page === "Alerts") return <AlertsManager onAlertsChanged={onAlertsChanged} />;
  if (page === "Safety Cases") return <SMSOverview allowedBases={allowedBases} />;
  if (page === "Accident Rate") return <AccidentRate allowedBases={allowedBases} />;
  if (page === "HFACS Analysis") return <SMSHFACSAnalysis allowedBases={allowedBases} />;
  if (page === "Weekly Digest") return <SafetyDigestSetup />;
  if (page === "Safety Culture Survey") {
    // safetyculture.manage is granted explicitly, admins included -- the server
    // checks it with admin_override=False, so an admin without the grant must not
    // be offered a button whose action would 403.
    return (
      <SafetyCultureSurvey
        canManage={allowedPermissions.has("safetyculture.manage")}
        // Triage is the wider grant of the two: it reads every comment, which
        // includes the ones somebody released. Either opens the card.
        canReadComments={allowedPermissions.has("safetyculture.comments.shared")
          || allowedPermissions.has("safetyculture.comments")}
        // Deciding what may be shared is the triage grant alone. Reading what
        // was already shared does not qualify anyone to make the decision.
        canTriage={allowedPermissions.has("safetyculture.comments")}
        onSetupReady={onSafetyCultureSetupReady}
        onTriageReady={onSafetyCultureTriageReady}
        onReportReady={onSafetyCultureReportReady}
      />
    );
  }
  if (page === "Departure & Arrival Analysis") {
    return <FlightOperations allowedBases={allowedBases} allowedPages={allowedPermissions} />;
  }
  if (page === "Tech Efficiency") {
    return (
      <TechEfficiency
        allowedBases={allowedBases}
        allowedPermissions={allowedPermissions}
        onMappingsReady={onTechEfficiencyMappingsReady}
      />
    );
  }
  if (page === "Lifecycles") {
    return <Lifecycles allowedPages={allowedPermissions} />;
  }
  if (page === "Course Completion") {
    // Reading the page is one grant; editing what it measures is another, and
    // the Set Up tab only appears for the second.
    return <CourseCompletion allowedPermissions={allowedPermissions} />;
  }
  if (page === "Cancellations") {
    return <Cancellations allowedBases={allowedBases} onExportReady={onCancellationsExportReady} />;
  }
  if (page === "Financial Statements") {
    // Which tabs: the statements with the books' grant, Term Debt and Borrowing
    // Base each with either of its own. Admins get none by being admins.
    return (
      <FinancialStatements
        onSettingsReady={onFinancialSettingsReady}
        showStatements={allowedPermissions.has("quickbooks.financials")}
        termDebt={allowedPermissions.has(TERM_DEBT_PERMISSION) || allowedPermissions.has(TERM_DEBT_UPLOAD_PERMISSION)}
        borrowingBase={allowedPermissions.has(BORROWING_BASE_PERMISSION) || allowedPermissions.has(BORROWING_BASE_UPLOAD_PERMISSION)}
      />
    );
  }
  const Component = PAGE_COMPONENTS[page];
  return Component ? <Component allowedBases={allowedBases} /> : null;
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("fox_token")}` };
}

/* A header dropdown that opens on hover for a mouse and on tap for a finger.

   It used to open on `:hover` alone, with no click handler on the trigger at
   all. iOS sometimes synthesises a hover on first tap, but that is a browser
   compatibility heuristic rather than an API: there was no way to close the
   menu again, no keyboard path in, and nothing exposed to a screen reader. All
   29 dashboards sat behind it.

   The panel is now mounted only while open. That is what removes the horizontal
   scroll from every page -- see the comment on .header-nav-dropdown in App.css.

   Mouse behaviour is deliberately unchanged: hover opens, leaving closes, and a
   click on the section name opens rather than toggles, so clicking the header
   the pointer is already resting on cannot make the menu vanish. */
function HeaderMenu({
  wrapClassName,
  triggerClassName,
  triggerContent,
  panelClassName,
  ariaLabel,
  children,
  /* Called on the gesture that opens the menu, so a section's first page can
     start downloading while the reader is still deciding. Optional -- the
     account menu has nothing to warm. */
  onPrefetch,
}) {
  const [open, setOpen] = React.useState(false);
  const canHover = useCanHover();
  const wrapRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const panelId = React.useId();

  // A menu can be left in more ways than it can be entered: a tap somewhere
  // else, Escape, or tabbing past the last item. All three close it.
  React.useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!wrapRef.current || wrapRef.current.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const hoverProps = canHover
    ? {
        onMouseEnter: () => {
          setOpen(true);
          onPrefetch?.();
        },
        onMouseLeave: () => setOpen(false),
      }
    : {};

  return (
    <div
      className={wrapClassName}
      ref={wrapRef}
      {...hoverProps}
      onBlur={(event) => {
        if (wrapRef.current?.contains(event.relatedTarget)) return;
        setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={ariaLabel}
        onClick={() => {
          onPrefetch?.();
          setOpen((wasOpen) => (canHover ? true : !wasOpen));
        }}
      >
        {triggerContent}
      </button>
      {open && (
        <div
          className={panelClassName}
          id={panelId}
          // Choosing anything in the menu dismisses it. Without this the panel
          // would stay open over the page it just navigated to on a touch
          // device, where no mouseleave is coming.
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function TimezoneSection({ currentUser, onUserUpdated, showHeading }) {
  const detected = React.useMemo(() => browserTimezone(), []);
  // "" is a real choice here: it clears the preference and follows the browser.
  const [selected, setSelected] = React.useState(currentUser?.timezone || "");
  const [message, setMessage] = React.useState({ type: "", text: "" });
  const [saving, setSaving] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);

  const effective = selected || detected;
  const now = React.useMemo(() => new Date(), []);

  const options = React.useMemo(() => {
    if (!showAll) return COMMON_TIMEZONES;
    return allTimezones().map((value) => ({ value, label: value.replace(/_/g, " ") }));
  }, [showAll]);

  // A saved zone that isn't in the curated list still needs to appear as the
  // current selection, or the dropdown would silently show the wrong thing.
  const optionsWithCurrent = React.useMemo(() => {
    if (!selected || options.some((o) => o.value === selected)) return options;
    return [{ value: selected, label: selected.replace(/_/g, " ") }, ...options];
  }, [options, selected]);

  const save = async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axios.patch(
        "/api/users/me/timezone",
        { timezone: selected || null },
        { headers: authHeaders() }
      );
      setMessage({ type: "success", text: res.data.message || "Timezone updated" });
      await onUserUpdated();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.detail || "Unable to update timezone.",
      });
    }
    setSaving(false);
  };

  const sample = React.useMemo(() => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: effective,
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      }).format(now);
    } catch {
      return "";
    }
  }, [effective, now]);

  return (
    <section className="card" style={{ padding: 18 }}>
      {showHeading && (
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Timezone</div>
      )}
      <div style={{ fontSize: 12.5, color: "var(--text2)", marginBottom: 14 }}>
        Dates and times across FOX are shown in this zone. Data is collected in UTC and
        converted for you.
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        <div className="field">
          <label>Show times in</label>
          <select value={selected} onChange={(e) => setSelected(e.target.value)}>
            <option value="">
              Follow my browser ({detected.replace(/_/g, " ")})
            </option>
            {optionsWithCurrent.map((zone) => (
              <option key={zone.value} value={zone.value}>
                {zone.label}
              </option>
            ))}
          </select>
        </div>

        {!showAll && (
          <button
            type="button"
            className="btn-link"
            style={{
              justifySelf: "start",
              background: "none",
              border: 0,
              padding: 0,
              color: "var(--blue)",
              cursor: "pointer",
              fontSize: 12.5,
            }}
            onClick={() => setShowAll(true)}
          >
            Show all timezones
          </button>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "var(--text2)",
            background: "var(--bg3)",
            borderRadius: "var(--radius)",
            padding: "9px 12px",
          }}
        >
          <Clock size={14} strokeWidth={1.75} />
          <span>
            Right now this reads <strong style={{ color: "var(--text)" }}>{sample}</strong>
          </span>
        </div>

        {message.text && (
          <div className={message.type === "error" ? "error-msg" : "success-msg"}>
            {message.text}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            className="btn-primary"
            onClick={save}
            disabled={saving || selected === (currentUser?.timezone || "")}
          >
            Save Timezone
          </button>
        </div>
      </div>
    </section>
  );
}

function UserSettingsModal({ currentUser, onClose, onUserUpdated, mode = "all" }) {
  const showEmail = mode === "all" || mode === "email";
  const showPassword = mode === "all" || mode === "password";
  const showTimezone = mode === "all" || mode === "timezone";
  const [emailForm, setEmailForm] = React.useState({ new_email: "", confirm_email: "" });
  const [passwordForm, setPasswordForm] = React.useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [emailMessage, setEmailMessage] = React.useState({ type: "", text: "" });
  const [passwordMessage, setPasswordMessage] = React.useState({ type: "", text: "" });
  const [savingEmail, setSavingEmail] = React.useState(false);
  const [savingPassword, setSavingPassword] = React.useState(false);

  const saveEmail = async () => {
    setSavingEmail(true);
    setEmailMessage({ type: "", text: "" });
    try {
      const res = await axios.patch("/api/users/me/email", emailForm, { headers: authHeaders() });
      setEmailMessage({ type: "success", text: res.data.message || "Email updated" });
      setEmailForm({ new_email: "", confirm_email: "" });
      await onUserUpdated();
    } catch (err) {
      setEmailMessage({
        type: "error",
        text: err.response?.data?.detail || "Unable to update email.",
      });
    }
    setSavingEmail(false);
  };

  const savePassword = async () => {
    setSavingPassword(true);
    setPasswordMessage({ type: "", text: "" });
    try {
      const res = await axios.patch("/api/users/me/password", passwordForm, {
        headers: authHeaders(),
      });
      setPasswordMessage({ type: "success", text: res.data.message || "Password updated" });
      setPasswordForm({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (err) {
      setPasswordMessage({
        type: "error",
        text: err.response?.data?.detail || "Unable to update password.",
      });
    }
    setSavingPassword(false);
  };

  const renderMessage = (message) => {
    if (!message.text) return null;
    return (
      <div className={message.type === "error" ? "error-msg" : "success-msg"}>
        {message.text}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-card"
        style={{ width: mode === "all" ? "min(720px, 100%)" : "min(460px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <div className="modal-title">
              {mode === "email"
                ? "Change Email"
                : mode === "password"
                  ? "Change Password"
                  : mode === "timezone"
                    ? "Timezone"
                    : "User Settings"}
            </div>
            <div className="modal-subtitle">
              {mode === "email"
                ? "Update the email address used to sign in to FOX."
                : mode === "password"
                  ? "Choose a new password for your FOX account."
                  : mode === "timezone"
                    ? "Choose the timezone FOX shows dates and times in."
                    : "Update your email, password, and timezone."}
            </div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close settings">
            X
          </button>
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          {showEmail && (
          <section className="card" style={{ padding: 18 }}>
            {mode === "all" && (
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Change Email</div>
            )}
            <div style={{ display: "grid", gap: 12 }}>
              <div className="field">
                <label>Current Email</label>
                <input type="email" value={currentUser?.email || ""} readOnly />
              </div>
              <div className="field">
                <label>New Email</label>
                <input
                  type="email"
                  value={emailForm.new_email}
                  onChange={(e) =>
                    setEmailForm((prev) => ({ ...prev, new_email: e.target.value }))
                  }
                />
              </div>
              <div className="field">
                <label>Confirm New Email</label>
                <input
                  type="email"
                  value={emailForm.confirm_email}
                  onChange={(e) =>
                    setEmailForm((prev) => ({ ...prev, confirm_email: e.target.value }))
                  }
                />
              </div>
              {renderMessage(emailMessage)}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="btn-primary" onClick={saveEmail} disabled={savingEmail}>
                  Save Email
                </button>
              </div>
            </div>
          </section>
          )}

          {showPassword && (
          <section className="card" style={{ padding: 18 }}>
            {mode === "all" && (
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Change Password</div>
            )}
            <div style={{ display: "grid", gap: 12 }}>
              <div className="field">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, current_password: e.target.value }))
                  }
                />
              </div>
              <div className="field">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, new_password: e.target.value }))
                  }
                />
              </div>
              <div className="field">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, confirm_password: e.target.value }))
                  }
                />
              </div>
              {renderMessage(passwordMessage)}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="btn-primary" onClick={savePassword} disabled={savingPassword}>
                  Save Password
                </button>
              </div>
            </div>
          </section>
          )}

          {showTimezone && (
            <TimezoneSection
              currentUser={currentUser}
              onUserUpdated={onUserUpdated}
              showHeading={mode === "all"}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* Shared by the home page's section list and by the phone navigation, which
   must show the same icon for the same dashboard in both places. */
const SECTION_ICONS = {
  "FLIGHT OPERATIONS": <Plane size={17} strokeWidth={1.75} />,
  MAINTENANCE: <Wrench size={17} strokeWidth={1.75} />,
  ACADEMICS: <GraduationCap size={17} strokeWidth={1.75} />,
  SAFETY: <ShieldCheck size={17} strokeWidth={1.75} />,
  FINANCE: <DollarSign size={17} strokeWidth={1.75} />,
  "RAMP OPERATIONS": <Fuel size={17} strokeWidth={1.75} />,
  Alerts: <Bell size={17} strokeWidth={1.75} />,
};

const PAGE_ICONS = {
  "Fleet Utilization Overview": BarChart3,
  "Training Hours": Clock,
  "Departure & Arrival Analysis": Plane,
  Cancellations: CalendarX,
  "Frequency Recordings": Radio,
  "Aircraft Status": PlaneTakeoff,
  "Simulator Status": Cpu,
  "Course Completion": Award,
  "BGSU Course Completion": Award,
  "Weather Conditions": Cloud,
  "Safety Cases": FileWarning,
  "Accident Rate": AlertTriangle,
  "HFACS Analysis": ListTree,
  "AirSync Health": RadioTower,
  "Maintenance Surveillance": Wrench,
  "Flight Training Revenue": LineChart,
  "Ramp Operations Overview": Truck,
  "Ramp Activity Records": ClipboardCheck,
  Alerts: Bell,
  "Maintenance Throughput": Activity,
  "Tech Efficiency": Wrench,
  Lifecycles: ListTree,
  "Oil Consumption": Fuel,
  "Maintenance Billing": DollarSign,
  "Financial Statements": FileSpreadsheet,
  "System Status": Activity,
};

const HOME_KPI_API_BASE = "";

function HomeView({ allowedPages, currentPage, setCurrentPage, currentUser }) {
  const [announcement, setAnnouncement] = React.useState(null);
  const [announcementManageOpen, setAnnouncementManageOpen] = React.useState(false);
  const [annTitle, setAnnTitle] = React.useState("");
  const [annBody, setAnnBody] = React.useState("");
  const [annExpires, setAnnExpires] = React.useState("");
  const [annSaving, setAnnSaving] = React.useState(false);
  const [annError, setAnnError] = React.useState("");



  const [kpiPrefs, setKpiPrefs] = React.useState([]);
  const [kpiValues, setKpiValues] = React.useState([]);
  const [kpiLoading, setKpiLoading] = React.useState(true);
  const [kpiError, setKpiError] = React.useState("");
  const [homeNotice, setHomeNotice] = React.useState("");
  const [customizeOpen, setCustomizeOpen] = React.useState(false);
  const [catalog, setCatalog] = React.useState([]);
  const [catalogLoading, setCatalogLoading] = React.useState(false);
  const [catalogError, setCatalogError] = React.useState("");
  const [draftSelected, setDraftSelected] = React.useState([]);
  const [scopeByKpi, setScopeByKpi] = React.useState({});
  const [draggedIndex, setDraggedIndex] = React.useState(null);
  const [savingPrefs, setSavingPrefs] = React.useState(false);
  const [customizeMessage, setCustomizeMessage] = React.useState("");
  const draftIdRef = React.useRef(0);

  const loadHomeKpis = React.useCallback(async () => {
    setKpiLoading(true);
    setKpiError("");
    try {
      const prefRes = await axios.get(`${HOME_KPI_API_BASE}/api/home/kpi-preferences`, { headers: authHeaders() });
      const prefs = prefRes.data?.preferences || [];
      setKpiPrefs(prefs);
      if (prefs.length === 0) {
        setKpiValues([]);
        setKpiLoading(false);
        return;
      }

      const requests = prefs.map((pref) => ({
        kpi_key: pref.kpi_key,
        location_scope: pref.location_scope,
      }));
      const valuesRes = await axios.get(`${HOME_KPI_API_BASE}/api/home/kpi-values`, {
        headers: authHeaders(),
        params: { requests: JSON.stringify(requests) },
      });
      setKpiValues(valuesRes.data?.values || []);
    } catch (err) {
      setKpiError("Unable to load home KPIs.");
      setKpiValues([]);
    } finally {
      setKpiLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadHomeKpis();
  }, [loadHomeKpis]);

  const loadAnnouncement = React.useCallback(async () => {
    try {
      const res = await axios.get(`${HOME_KPI_API_BASE}/api/announcements/active`, { headers: authHeaders() });
      setAnnouncement(res.data?.announcement || null);
    } catch (err) {
      setAnnouncement(null);
    }
  }, []);
  React.useEffect(() => { loadAnnouncement(); }, [loadAnnouncement]);

  const submitAnnouncement = async () => {
    setAnnError("");
    if (!annTitle.trim()) { setAnnError("Title is required."); return; }
    if (!annBody.trim()) { setAnnError("Message is required."); return; }
    setAnnSaving(true);
    try {
      await axios.post(
        `${HOME_KPI_API_BASE}/api/announcements`,
        { title: annTitle.trim(), body: annBody.trim(), expires_at: annExpires ? new Date(annExpires).toISOString() : null },
        { headers: authHeaders() }
      );
      setAnnouncementManageOpen(false);
      setAnnTitle(""); setAnnBody(""); setAnnExpires("");
      await loadAnnouncement();
    } catch (err) {
      setAnnError(err.response?.data?.detail || "Unable to post announcement.");
    } finally {
      setAnnSaving(false);
    }
  };

  const dismissAnnouncement = async () => {
    if (!announcement) return;
    setAnnSaving(true);
    setAnnError("");
    try {
      await axios.delete(`${HOME_KPI_API_BASE}/api/announcements/${announcement.id}`, { headers: authHeaders() });
      setAnnouncementManageOpen(false);
      await loadAnnouncement();
    } catch (err) {
      setAnnError(err.response?.data?.detail || "Unable to remove announcement.");
    } finally {
      setAnnSaving(false);
    }
  };

  const dismissAnnouncementForUser = async () => {
    if (!announcement) return;
    const dismissedId = announcement.id;
    setAnnouncement(null); // optimistic hide
    try {
      await axios.post(`${HOME_KPI_API_BASE}/api/announcements/${dismissedId}/dismiss`, {}, { headers: authHeaders() });
    } catch (err) {
      // if it fails, reload so the banner reappears rather than silently staying hidden
      await loadAnnouncement();
    }
  };

  const greeting = React.useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  const firstName = React.useMemo(() => {
    const name = currentUser?.full_name || currentUser?.email || "";
    return name.trim().split(/\s+/)[0] || "there";
  }, [currentUser]);

  const canManageAnnouncements = React.useMemo(() => {
    const pages = currentUser?.allowed_pages || [];
    return Boolean(currentUser?.is_admin) || pages.includes("admin.announcements.manage");
  }, [currentUser]);

  const formatAnnouncementDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
  };

  const valuesByRequest = React.useMemo(() => {
    const map = new Map();
    kpiValues.forEach((item) => {
      map.set(`${item.kpi_key}:${item.location_scope}`, item);
    });
    return map;
  }, [kpiValues]);

  const formatKpiValue = (value, format) => {
    if (value === null || value === undefined || value === "") return "—";
    if (format === "percent") return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    if (format === "days") return `${Number(value).toLocaleString()} days`;
    if (format === "currency") {
      return Number(value).toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });
    }
    if (format === "fraction" && typeof value === "object") {
      return `${value.value ?? value.numerator ?? 0} of ${value.total ?? value.denominator ?? 0}`;
    }
    if (format === "hours_yoy") {
      const hrs = value && typeof value === "object" ? value.value : value;
      if (hrs === null || hrs === undefined) return "—";
      return `${Number(hrs).toLocaleString()} hrs`;
    }
    return Number(value).toLocaleString();
  };

  const locationLabel = (scope) => (scope === "ALL" ? "All locations" : scope);
  const catalogByKey = React.useMemo(() => {
    const map = new Map();
    catalog.forEach((item) => map.set(item.key, item));
    return map;
  }, [catalog]);
  const groupedCatalog = React.useMemo(() => {
    const order = ["Aircraft", "Fleet", "Safety", "Simulators", "Operations", "Other"];
    const groups = new Map();
    catalog.forEach((item) => {
      const category = item.category || "Other";
      if (!groups.has(category)) groups.set(category, []);
      groups.get(category).push(item);
    });
    return [
      ...order.filter((category) => groups.has(category)),
      ...Array.from(groups.keys()).filter((category) => !order.includes(category)).sort(),
    ].map((category) => ({ category, items: groups.get(category) }));
  }, [catalog]);

  const makeDraftItem = React.useCallback((pref) => {
    draftIdRef.current += 1;
    return {
      local_id: `draft-${draftIdRef.current}`,
      kpi_key: pref.kpi_key,
      location_scope: pref.location_scope,
    };
  }, []);

  const loadCustomizeCatalog = React.useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError("");
    setCustomizeMessage("");
    setDraftSelected(kpiPrefs.map(makeDraftItem));
    try {
      const res = await axios.get(`${HOME_KPI_API_BASE}/api/home/kpi-catalog`, { headers: authHeaders() });
      const items = res.data?.kpis || [];
      setCatalog(items);
      setScopeByKpi((prev) => {
        const next = { ...prev };
        items.forEach((item) => {
          if (!next[item.key]) next[item.key] = item.available_location_scopes?.[0] || "ALL";
        });
        return next;
      });
    } catch (err) {
      setCatalogError(err.response?.data?.detail || "Unable to load available KPIs.");
    } finally {
      setCatalogLoading(false);
    }
  }, [kpiPrefs, makeDraftItem]);

  const handleCustomizeHome = React.useCallback(() => {
    setCustomizeOpen(true);
    loadCustomizeCatalog();
  }, [loadCustomizeCatalog]);

  const addDraftKpi = (kpi) => {
    if (draftSelected.length >= 12) {
      setCustomizeMessage("You can save up to 12 KPI tiles.");
      return;
    }
    const scope = kpi.available_location_scopes?.length === 1
      ? kpi.available_location_scopes[0]
      : scopeByKpi[kpi.key] || kpi.available_location_scopes?.[0] || "ALL";
    setDraftSelected((prev) => [...prev, makeDraftItem({ kpi_key: kpi.key, location_scope: scope })]);
    setCustomizeMessage("");
  };

  const removeDraftKpi = (localId) => {
    setDraftSelected((prev) => prev.filter((item) => item.local_id !== localId));
    setCustomizeMessage("");
  };

  const moveDraftKpi = (fromIndex, toIndex) => {
    if (fromIndex === null || fromIndex === toIndex) return;
    setDraftSelected((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setDraggedIndex(null);
  };

  const saveHomeKpiPreferences = async () => {
    setSavingPrefs(true);
    setCustomizeMessage("");
    try {
      await axios.put(
        `${HOME_KPI_API_BASE}/api/home/kpi-preferences`,
        draftSelected.map((item) => ({
          kpi_key: item.kpi_key,
          location_scope: item.location_scope,
        })),
        { headers: authHeaders() }
      );
      setCustomizeOpen(false);
      setHomeNotice("Home KPIs saved.");
      window.setTimeout(() => setHomeNotice(""), 2200);
      await loadHomeKpis();
    } catch (err) {
      setCustomizeMessage(err.response?.data?.detail || "Unable to save home KPIs.");
    } finally {
      setSavingPrefs(false);
    }
  };

  return (
    <div className="fox-home">
      <div className="fox-home-hero">
        <div>
          <div className="fox-home-greeting">{greeting}, {firstName}</div>
        </div>
        <button
          type="button"
          className="fox-home-customize"
          onClick={handleCustomizeHome}
          aria-label="Customize home"
          title="Customize home"
        >
          <Settings size={16} strokeWidth={1.8} />
          <span>Edit KPI Cards</span>
        </button>
      </div>

      {homeNotice && <div className="fox-home-notice">{homeNotice}</div>}

      {(announcement || canManageAnnouncements) && (
        <section className="fox-home-announcement-wrap">
          {announcement ? (
            <div className="fox-home-announcement">
              <span className="fox-home-announcement-icon">
                <Megaphone size={18} strokeWidth={1.9} />
              </span>
              <div className="fox-home-announcement-body">
                <div className="fox-home-announcement-eyebrow">Announcement</div>
                <div className="fox-home-announcement-title">{announcement.title}</div>
                <div className="fox-home-announcement-text">{announcement.body}</div>
                <div className="fox-home-announcement-meta">
                  <span>Posted {formatAnnouncementDate(announcement.published_at)}</span>
                  {announcement.expires_at && <span>Expires {formatAnnouncementDate(announcement.expires_at)}</span>}
                  {announcement.created_by_name && <span>By {announcement.created_by_name}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
                {canManageAnnouncements && (
                  <button type="button" className="fox-home-announcement-manage" onClick={() => setAnnouncementManageOpen(true)} title="Manage announcement" aria-label="Manage announcement">
                    <Settings size={15} strokeWidth={1.8} />
                  </button>
                )}
                <button type="button" className="fox-home-announcement-manage" onClick={dismissAnnouncementForUser} title="Dismiss" aria-label="Dismiss announcement">
                  <X size={15} strokeWidth={1.8} />
                </button>
              </div>
            </div>
          ) : canManageAnnouncements ? (
            <button type="button" className="btn-secondary" onClick={() => setAnnouncementManageOpen(true)} style={{ width: "fit-content" }}>
              <Megaphone size={15} strokeWidth={1.8} style={{ marginRight: 6, verticalAlign: "-2px" }} />
              Post an announcement
            </button>
          ) : null}
        </section>
      )}

      <section className="fox-home-kpi-section">
        {kpiPrefs.length === 0 && kpiLoading ? (
          <div className="fox-home-kpi-grid">
            {[0, 1, 2, 3].map((idx) => (
              <div key={idx} className="fox-home-kpi-card fox-home-kpi-skeleton" />
            ))}
          </div>
        ) : kpiPrefs.length === 0 ? (
          <div className="fox-home-kpi-empty">
            <span>Add KPIs to your home page to see key metrics at a glance.</span>
            <button type="button" className="btn-primary" onClick={handleCustomizeHome}>Add KPIs</button>
          </div>
        ) : (
          <>
            {kpiError && <div className="fox-home-kpi-error">{kpiError}</div>}
            <div className="fox-home-kpi-grid">
              {kpiPrefs.map((pref) => {
                const valueItem = valuesByRequest.get(`${pref.kpi_key}:${pref.location_scope}`);
                const catalogItem = catalogByKey.get(pref.kpi_key);
                const description = valueItem?.description || catalogItem?.description || "";
                const isLoading = kpiLoading && !valueItem;
                return (
                  <div key={pref.id} className={`fox-home-kpi-card${isLoading ? " is-loading" : ""}`} title={description}>
                    <div className="fox-home-kpi-topline">
                      <span className="fox-home-kpi-label" title={description}>{pref.label || valueItem?.label || pref.kpi_key}</span>
                      <span className={`fox-home-location-badge fox-home-location-badge--${pref.location_scope.toLowerCase()}`}>
                        {locationLabel(pref.location_scope)}
                      </span>
                    </div>
                    {isLoading ? (
                      <div className="fox-home-kpi-value-loading" aria-label="Loading" />
                    ) : (
                      <>
                        <div className="fox-home-kpi-value">
                      {valueItem ? formatKpiValue(valueItem.value, valueItem.format) : "—"}
                        </div>
                        {valueItem?.format === "hours_yoy" &&
                          valueItem?.value &&
                          valueItem.value.delta_pct !== null &&
                          valueItem.value.delta_pct !== undefined && (
                            <div className={`fox-home-kpi-delta ${valueItem.value.delta_pct >= 0 ? "is-up" : "is-down"}`}>
                              {valueItem.value.delta_pct >= 0 ? "▲" : "▼"}{" "}
                              {Math.abs(valueItem.value.delta_pct).toLocaleString(undefined, { maximumFractionDigits: 1 })}% YoY
                            </div>
                          )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      <div className="fox-home-nav">
        {Object.entries(SECTIONS)
          .filter(([, pages]) => sectionPages(pages).some((page) => allowedPages.has(page)))
          .map(([section, pages]) => (
            <section key={section} className="fox-home-section">
              <div className="fox-home-section-header">
                <span className="fox-home-section-icon">
                  {SECTION_ICONS[section] || null}
                </span>
                <span>{section}</span>
              </div>
              <div className="fox-home-links">
                {sectionPages(pages)
                  .filter((pageName) => allowedPages.has(pageName))
                  .map((pageName) => {
                    const PageIcon = PAGE_ICONS[pageName] || BarChart3;
                    return (
                      <button
                        key={pageName}
                        className={`fox-home-link${currentPage === pageName ? " active" : ""}`}
                        onClick={() => setCurrentPage(pageName)}
                      >
                        <span className="fox-home-link-icon-chip">
                          <PageIcon className="fox-home-link-icon" size={17} strokeWidth={1.8} />
                        </span>
                        <span className="fox-home-link-title">{pageLabel(pageName)}</span>
                      </button>
                    );
                  })}
              </div>
            </section>
          ))}
      </div>

      {customizeOpen && (
        <div className="modal-overlay" onClick={() => setCustomizeOpen(false)}>
          <div className="modal-card fox-home-customize-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header fox-home-customize-header">
              <div>
                <div className="modal-title">Customize home KPIs</div>
                <div className="modal-subtitle">Choose the metrics and location scopes shown on your home page.</div>
              </div>
              <button type="button" className="modal-close" onClick={() => setCustomizeOpen(false)} aria-label="Close customize panel">
                <X size={17} />
              </button>
            </div>

            <div className="fox-home-customize-content">
            <div className="fox-home-customize-layout">
              <section className="fox-home-customize-pane">
                <div className="fox-home-customize-pane-title">Available KPIs</div>
                {catalogLoading ? (
                  <div className="fox-home-customize-muted">Loading available KPIs...</div>
                ) : catalogError ? (
                  <div className="error-msg">{catalogError}</div>
                ) : (
                  <div className="fox-home-catalog-list">
                    {groupedCatalog.map((group) => (
                      <div key={group.category} className="fox-home-catalog-group">
                        <div className="fox-home-catalog-group-title">{group.category}</div>
                        {group.items.map((kpi) => {
                          const scopes = kpi.available_location_scopes || [];
                          return (
                            <div key={kpi.key} className="fox-home-catalog-row">
                              <div className="fox-home-catalog-main">
                                <div className="fox-home-catalog-label" title={kpi.description || ""}>
                                  {kpi.label}
                                  {kpi.description && (
                                    <Info size={13} strokeWidth={1.8} style={{ marginLeft: 5, verticalAlign: "-2px" }} />
                                  )}
                                </div>
                                <div className="fox-home-catalog-format">{kpi.format}</div>
                              </div>
                              {scopes.length > 1 && (
                                <FilterDropdown
                                  value={scopeByKpi[kpi.key] || scopes[0]}
                                  onChange={(event) => setScopeByKpi((prev) => ({ ...prev, [kpi.key]: event.target.value }))}
                                  options={scopes.map((scope) => ({ value: scope, label: locationLabel(scope) }))}
                                />
                              )}
                              <button
                                type="button"
                                className="fox-home-compact-primary"
                                onClick={() => addDraftKpi(kpi)}
                                disabled={draftSelected.length >= 12}
                              >
                                <Plus size={14} />
                                Add
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="fox-home-customize-pane">
                <div className="fox-home-customize-pane-title">
                  Selected KPIs
                  <span>{draftSelected.length}/12</span>
                </div>
                {draftSelected.length === 0 ? (
                  <div className="fox-home-selected-empty">No KPIs selected yet.</div>
                ) : (
                  <div className="fox-home-selected-list">
                    {draftSelected.map((item, index) => {
                      const kpi = catalogByKey.get(item.kpi_key);
                      return (
                        <div
                          key={item.local_id}
                          className="fox-home-selected-row"
                          /* Drag stays for the mouse. It is not the only way to
                             reorder any more, because HTML5 drag-and-drop does
                             not exist on iOS Safari -- a phone user could add
                             and remove KPIs but never order them. The buttons
                             below cover that, and give the list a keyboard path
                             it never had. */
                          draggable
                          onDragStart={() => setDraggedIndex(index)}
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={() => moveDraftKpi(draggedIndex, index)}
                          onDragEnd={() => setDraggedIndex(null)}
                        >
                          <span className="fox-home-drag-handle" aria-hidden="true">
                            <GripVertical size={16} />
                          </span>
                          <div className="fox-home-selected-main">
                            <span className="fox-home-selected-label">{kpi?.label || item.kpi_key}</span>
                            <span className={`fox-home-location-badge fox-home-location-badge--${item.location_scope.toLowerCase()}`}>
                              {locationLabel(item.location_scope)}
                            </span>
                          </div>
                          <div className="fox-home-reorder">
                            <button
                              type="button"
                              className="fox-home-reorder-btn"
                              disabled={index === 0}
                              onClick={() => moveDraftKpi(index, index - 1)}
                              aria-label={`Move ${kpi?.label || item.kpi_key} up`}
                            >
                              <ChevronUp size={16} strokeWidth={2.2} />
                            </button>
                            <button
                              type="button"
                              className="fox-home-reorder-btn"
                              disabled={index === draftSelected.length - 1}
                              onClick={() => moveDraftKpi(index, index + 1)}
                              aria-label={`Move ${kpi?.label || item.kpi_key} down`}
                            >
                              <ChevronDown size={16} strokeWidth={2.2} />
                            </button>
                          </div>
                          <button
                            type="button"
                            className="fox-home-selected-remove"
                            onClick={() => removeDraftKpi(item.local_id)}
                            aria-label={`Remove ${kpi?.label || item.kpi_key}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
            </div>

            <div className="fox-home-customize-actions">
              {customizeMessage && <div className="fox-home-customize-message error-msg">{customizeMessage}</div>}
              <button type="button" className="btn-ghost" onClick={() => setCustomizeOpen(false)} disabled={savingPrefs}>
                Cancel
              </button>
              <button type="button" className="fox-home-compact-primary" onClick={saveHomeKpiPreferences} disabled={savingPrefs}>
                {savingPrefs ? "Saving" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {announcementManageOpen && (
        <div className="modal-overlay" onClick={() => setAnnouncementManageOpen(false)}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div className="modal-title">Manage Announcement</div>
              <button type="button" className="modal-close" onClick={() => setAnnouncementManageOpen(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
              {announcement && (
                <div style={{ padding: "10px 12px", background: "var(--bg3)", borderRadius: "var(--radius)", fontSize: 12, color: "var(--text2)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <span>A message is currently live. Posting a new one replaces it.</span>
                  <button type="button" className="btn-secondary" onClick={dismissAnnouncement} disabled={annSaving} style={{ flexShrink: 0 }}>Remove current</button>
                </div>
              )}
              <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>
                Title
                <input type="text" value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} maxLength={200} placeholder="e.g. Scheduled maintenance this weekend" style={{ width: "100%", marginTop: 6, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: "var(--radius)", fontSize: 14 }} />
              </label>
              <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>
                Message
                <textarea value={annBody} onChange={(e) => setAnnBody(e.target.value)} maxLength={8000} rows={5} placeholder="Write the announcement users will see when they log in." style={{ width: "100%", marginTop: 6, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: "var(--radius)", fontSize: 14, resize: "vertical", fontFamily: "inherit" }} />
              </label>
              <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>
                Expires (optional)
                <input type="datetime-local" value={annExpires} onChange={(e) => setAnnExpires(e.target.value)} style={{ width: "100%", marginTop: 6, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: "var(--radius)", fontSize: 14 }} />
                <span style={{ display: "block", marginTop: 4, fontSize: 11, fontWeight: 400, color: "var(--text3)" }}>Leave blank for a message that stays until removed. The publish date and time are set automatically.</span>
              </label>
              {annError && <div style={{ fontSize: 12, color: "var(--red, var(--ink-red))", fontWeight: 600 }}>{annError}</div>}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
                <button type="button" className="btn-secondary" onClick={() => setAnnouncementManageOpen(false)} disabled={annSaving}>Cancel</button>
                <button type="button" className="btn-primary" onClick={submitAnnouncement} disabled={annSaving}>{annSaving ? "Posting..." : "Post announcement"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [currentPage, setCurrentPage] = React.useState(pageFromLocation);
  // Declared here, with the other hooks, because App() returns <Login/> early
  // when nobody is signed in -- a hook below that point would be skipped on
  // some renders.
  const isPhone = useIsPhone();
  // A touch screen wide enough for the desktop layout: the rail replaces the
  // header menu there, the way the tab bar does on a phone.
  const isTablet = useIsTablet();
  // Drives the toggle's icon and label only. The theme itself is applied by the
  // data-theme attribute on <html>, so no styling below depends on this.
  const isDarkMode = useIsDark();
  const [currentUser, setCurrentUser] = React.useState(null);
  const [allowedBases, setAllowedBases] = React.useState(["BGFC", "NSA", "PFC"]);
  const [settingsMode, setSettingsMode] = React.useState(null);
  const [navLinks, setNavLinks] = React.useState([]);
  const [showHelp, setShowHelp] = React.useState(false);
  const helpFrameRef = React.useRef(null);
  /* Pinned when the drawer OPENS, by openHelp below -- not read from isDarkMode
     during render. React reloads an iframe whose src changes, and a reload
     mid-form loses whatever the person had typed, so the src has to hold still
     for as long as the drawer is up. The drawer is unmounted when closed, so
     the next open builds a fresh iframe and picks this up.

     It was pinned at component mount before, which is wrong for the ordinary
     case of toggling the theme and then opening Help: the src carried the theme
     from page load, and the panel opened in the wrong one. */
  const helpFrameTheme = React.useRef(isDarkMode ? "dark" : "light");
  const openHelp = React.useCallback(() => {
    helpFrameTheme.current = isDarkMode ? "dark" : "light";
    setShowHelp(true);
  }, [isDarkMode]);

  /* Covers a toggle while the drawer is already up, where the src cannot
     change. postHelpTheme is also wired to the iframe's onLoad: posting into a
     frame that has not finished parsing is a message with no listener yet, and
     silently lost. */
  const postHelpTheme = React.useCallback(() => {
    const frame = helpFrameRef.current;
    if (!frame || !frame.contentWindow) return;
    // Same-origin, so this is the narrowest target that still reaches it.
    frame.contentWindow.postMessage(
      { foxTheme: isDarkMode ? "dark" : "light" },
      window.location.origin
    );
  }, [isDarkMode]);

  React.useEffect(() => {
    postHelpTheme();
  }, [postHelpTheme, showHelp]);

  const [showFlightsUpload, setShowFlightsUpload] = React.useState(false);
  const [showTechEfficiencyUpload, setShowTechEfficiencyUpload] = React.useState(false);
  const [showDashboardAlertModal, setShowDashboardAlertModal] = React.useState(false);
  const [freshness, setFreshness] = React.useState(null);
  const [sessionNotice, setSessionNotice] = React.useState("");
  const [dashboardHeaderAlerts, setDashboardHeaderAlerts] = React.useState([]);
  const [cancellationsExportHandler, setCancellationsExportHandler] = React.useState(null);
  const [techEfficiencyMappingsHandler, setTechEfficiencyMappingsHandler] = React.useState(null);
  const [safetyCultureSetupHandler, setSafetyCultureSetupHandler] = React.useState(null);
  const [safetyCultureTriageHandler, setSafetyCultureTriageHandler] = React.useState(null);
  const [safetyCultureReportHandler, setSafetyCultureReportHandler] = React.useState(null);
  const [financialSettingsHandler, setFinancialSettingsHandler] = React.useState(null);
  const [activeAlertManagerCount, setActiveAlertManagerCount] = React.useState(0);
  // The user's saved zone, or the browser's until they pick one.
  const userTimezone = currentUser?.timezone || browserTimezone();

  const [clockTick, setClockTick] = React.useState(() => new Date());
  React.useEffect(() => {
    const timer = window.setInterval(() => setClockTick(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  // Header clock carries its zone abbreviation, so it is obvious at a glance
  // which clock every other timestamp on the page is being read against.
  const timestamp = React.useMemo(() => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: userTimezone,
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      }).format(clockTick);
    } catch {
      return clockTick.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  }, [clockTick, userTimezone]);

  const fetchCurrentUser = React.useCallback(async () => {
    const token = localStorage.getItem("fox_token");
    if (!token) return;
    try {
      const res = await axios.get("/auth/me", { headers: authHeaders() });
      setCurrentUser(res.data);
      setAllowedBases(res.data.allowed_bases || ["BGFC", "NSA", "PFC"]);
    } catch {
      localStorage.removeItem("fox_token");
    }
  }, []);

  React.useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const fetchActiveAlertManagerCount = React.useCallback(() => {
    if (!localStorage.getItem("fox_token")) {
      setActiveAlertManagerCount(0);
      return;
    }
    axios
      .get("/api/alerts", { headers: authHeaders() })
      .then((res) => {
        const alerts = res.data?.alerts || [];
        setActiveAlertManagerCount(
          alerts.filter((alert) => alert.state !== "paused" && alert.state !== "retired").length
        );
      })
      .catch(() => setActiveAlertManagerCount(0));
  }, []);

  React.useEffect(() => {
    if (currentUser) {
      fetchActiveAlertManagerCount();
    }
  }, [currentUser, fetchActiveAlertManagerCount]);

  const activeAlertDashboardKey = PAGE_ALERT_DASHBOARD[currentPage];

  const fetchDashboardHeaderAlerts = React.useCallback(() => {
    if (!localStorage.getItem("fox_token") || !activeAlertDashboardKey) {
      setDashboardHeaderAlerts([]);
      return;
    }
    axios
      .get(`/api/alerts/dashboard/${activeAlertDashboardKey}`, { headers: authHeaders() })
      .then((res) => setDashboardHeaderAlerts(res.data.alerts || []))
      .catch(() => setDashboardHeaderAlerts([]));
  }, [activeAlertDashboardKey]);

  React.useEffect(() => {
    fetchDashboardHeaderAlerts();
  }, [fetchDashboardHeaderAlerts]);

  React.useEffect(() => {
    setFreshness(null);
    if (!localStorage.getItem("fox_token")) {
      return undefined;
    }
    let cancelled = false;
    axios
      .get("/api/data-freshness", {
        params: { page: currentPage },
        headers: authHeaders(),
      })
      .then((res) => {
        if (!cancelled) setFreshness(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setFreshness(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentPage]);

  React.useEffect(() => {
    const handlePopState = () => setCurrentPage(pageFromLocation());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  React.useEffect(() => {
    if (!currentUser) return undefined;
    let cancelled = false;
    axios
      .get("/api/nav-links", { headers: authHeaders() })
      .then((res) => {
        if (!cancelled) setNavLinks(res.data?.links || []);
      })
      .catch(() => {
        if (!cancelled) setNavLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  React.useEffect(() => {
    const targetPath = PAGE_TO_PATH[currentPage] || "/";
    if (window.location.pathname !== targetPath) {
      const method = "pushState";
      window.history[method]({}, "", targetPath);
    }
  }, [currentPage]);

  const handleLogout = React.useCallback(() => {
    localStorage.removeItem("fox_token");
    setCurrentUser(null);
    setAllowedBases(["BGFC", "NSA", "PFC"]);
    setCurrentPage("Home");
    setSettingsMode(null);
    setActiveAlertManagerCount(0);
  }, []);

  // Global 401 handler: when the JWT expires mid-session, every axios call in
  // the app starts returning 401. Log out once, centrally, and tell the user
  // why — instead of each dashboard failing in its own way until a hard
  // refresh. Skips /auth/login (a 401 there is just a wrong password), and
  // only fires when a token existed, so it can't misread a failed login as an
  // expired session. Errors are still re-thrown so components' own catch
  // blocks behave as before.
  React.useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = String(error?.config?.url || "");
        const hadToken = Boolean(localStorage.getItem("fox_token"));
        if (status === 401 && hadToken && !url.startsWith("/auth/login")) {
          handleLogout();
          setSessionNotice("Your session expired — please sign in again.");
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [handleLogout]);

  if (!currentUser) {
    return (
      <Login
        notice={sessionNotice}
        onLogin={(user) => {
          setSessionNotice("");
          setCurrentUser(user);
          setAllowedBases(user.allowed_bases || ["BGFC", "NSA", "PFC"]);
        }}
      />
    );
  }

  const isAdmin = Boolean(currentUser.is_admin);
  const userPages = currentUser.allowed_pages || [];
  const dashboardAllowedBases = isAdmin ? ADMIN_BASES : allowedBases;
  const canAccessAdmin = Boolean(currentUser?.is_admin) || userPages.some((p) => String(p).startsWith("admin."));
  const allowedPermissions = new Set(userPages);
  // allowed_pages carries no admin inheritance, so is_admin is checked here the
  // same way canManageAnnouncements does it.
  const canManageTickets = isAdmin || allowedPermissions.has("admin.tickets.manage");
  const canUploadFlights = Boolean(allowedPermissions.has("flights.upload"));
  const canUploadTechEfficiency = Boolean(allowedPermissions.has("techefficiency.upload"));
  const canManageTechEfficiencyMappings = Boolean(allowedPermissions.has("techefficiency.mappings"));
  const access = {
    isAdmin,
    permissions: allowedPermissions,
    overrides: PAGE_PERMISSION_OVERRIDES,
    legacy: LEGACY_PAGE_PERMISSIONS,
    explicitGrant: EXPLICIT_GRANT_PAGES,
    alsoOpenedBy: ALSO_OPENED_BY,
  };
  const allowedPages = new Set(Array.from(builtPages).filter((page) => pageIsAllowed(page, access)));
  // Pages still in development that this user can open (every one, for an
  // admin). /auth/me names them by permission key, which is not always the
  // page's title, so map through the same overrides the access check uses.
  const previewKeys = new Set(currentUser.preview_pages || []);
  const isPreview = (page) => previewKeys.has(PAGE_PERMISSION_OVERRIDES[page] || page);

  /* The section list, filtered to what this user can actually open, built once
     and read by both navigations. The header menu and the phone tab bar used
     to be free to disagree about what exists; sharing the list is what stops
     a dashboard appearing in one and not the other. */
  const visibleSections = Object.entries(SECTIONS)
    .map(([sectionName, pages]) => [
      sectionName,
      sectionPages(pages).filter((page) => builtPages.has(page) && allowedPages.has(page)),
    ])
    .filter(([, pages]) => pages.length > 0);

  if (
    (currentPage === "Admin" && !canAccessAdmin) ||
    (currentPage !== "Home" && currentPage !== "Admin" && !allowedPages.has(currentPage))
  ) {
    setCurrentPage("Home");
    return null;
  }

  const pageContent = renderDashboardPage(
    currentPage,
    allowedPermissions,
    dashboardAllowedBases,
    fetchActiveAlertManagerCount,
    setCancellationsExportHandler,
    setTechEfficiencyMappingsHandler,
    canManageTickets,
    setSafetyCultureSetupHandler,
    setSafetyCultureTriageHandler,
    setFinancialSettingsHandler,
    setSafetyCultureReportHandler
  );
  const activeDashboardAlerts = dashboardHeaderAlerts.filter(
    (alert) => alert.state !== "paused" && alert.state !== "retired"
  );
  const activeDashboardAlertCount = activeDashboardAlerts.length;
  const hasDashboardAlerts = dashboardHeaderAlerts.length > 0;
  const hasActiveDashboardAlerts = activeDashboardAlertCount > 0;
  const dashboardAlertsMuted = hasDashboardAlerts && !hasActiveDashboardAlerts;
  const DashboardAlertIcon = dashboardAlertsMuted ? BellOff : Bell;
  const dashboardAlertTitle = hasActiveDashboardAlerts
    ? `${activeDashboardAlertCount} active alert${activeDashboardAlertCount === 1 ? "" : "s"}`
    : dashboardAlertsMuted
      ? "Alerts configured but none active"
      : "No alerts here — click to create one";
  const dashboardAlertButtonStyle = hasActiveDashboardAlerts
    ? { color: "var(--blue)", background: "var(--blue-dim)", position: "relative" }
    : dashboardAlertsMuted
      ? { color: "#777", opacity: 0.85, position: "relative" }
      : { color: "#777", position: "relative" };

  return (
    <TimezoneProvider preference={currentUser?.timezone}>
    <div id="fox-app">
      <header id="fox-header">
        <div className="header-brand">
          <button
            onClick={() => setCurrentPage("Home")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <img src={logoImg} alt="FOX" className="header-logo" />
            <span className="header-brand-name">FOX</span>
          </button>
        </div>

        {!isPhone && !isTablet && (
        <nav className="header-nav">
          {visibleSections.map(([sectionName, visiblePages]) => {
            if (DIRECT_NAV_ITEMS.has(sectionName)) {
              const pageName = visiblePages[0];
              return (
                <div className="header-nav-section" key={sectionName}>
                  <button
                    className={`header-nav-section-btn${currentPage === pageName ? " active" : ""}`}
                    onClick={() => setCurrentPage(pageName)}
                    style={{ gap: 6 }}
                  >
                    <span style={{ position: "relative", display: "inline-flex", color: "var(--blue)" }}>
                      <Bell size={15} strokeWidth={1.8} />
                      {activeAlertManagerCount > 0 && (
                        <span
                          style={{
                            position: "absolute",
                            top: -3,
                            right: -3,
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: "var(--blue)",
                          }}
                        />
                      )}
                    </span>
                    {pageLabel(pageName)}
                  </button>
                </div>
              );
            }
            const sectionActive = visiblePages.includes(currentPage);
            return (
              <HeaderMenu
                key={sectionName}
                onPrefetch={() => prefetchPage(visiblePages[0])}
                wrapClassName="header-nav-section"
                triggerClassName={`header-nav-section-btn${sectionActive ? " active" : ""}`}
                panelClassName="header-nav-dropdown"
                triggerContent={
                  <>
                    {sectionName}
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      style={{ marginLeft: 2, opacity: 0.5 }}
                    >
                      <path
                        d="M2 3.5L5 6.5L8 3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                }
              >
                {visiblePages.map((pageName) => {
                  return (
                    <button
                      key={pageName}
                      className={`header-nav-dropdown-item${currentPage === pageName ? " active" : ""}`}
                      // Opening the menu and reading down it is a second or
                      // more of warning before the click. Spending it on the
                      // fetch is what keeps a split build feeling like the
                      // unsplit one on a desktop.
                      onPointerEnter={() => prefetchPage(pageName)}
                      onFocus={() => prefetchPage(pageName)}
                      onClick={() => setCurrentPage(pageName)}
                    >
                      {pageLabel(pageName)}
                      {isPreview(pageName) && (
                        <span
                          title="Still in development. Only people an admin chose can open it."
                          style={{ marginLeft: 8, padding: "1px 6px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: "var(--amber-dim)", color: "var(--ink-amber)" }}
                        >
                          Preview
                        </span>
                      )}
                    </button>
                  );
                })}
              </HeaderMenu>
            );
          })}

          {navLinks.length > 0 && (
            <HeaderMenu
              wrapClassName="header-nav-section"
              triggerClassName="header-nav-section-btn"
              panelClassName="header-nav-dropdown"
              triggerContent={
                <>
                  LINKS
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    style={{ marginLeft: 2, opacity: 0.5 }}
                  >
                    <path
                      d="M2 3.5L5 6.5L8 3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              }
            >
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  className="header-nav-dropdown-item"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink
                    size={14}
                    strokeWidth={1.8}
                    style={{ marginRight: 9, flexShrink: 0, opacity: 0.6 }}
                  />
                  {link.display_name}
                </a>
              ))}
            </HeaderMenu>
          )}
        </nav>
        )}

        <div className="header-right">
          {/* Left of the help button, which is the way in to raising a ticket.
              A button rather than a switch: there are two states and the icon
              shows which one you are in, so aria-pressed carries the state and
              the label says what the click will do. */}
          <button
            type="button"
            className="header-theme-btn"
            onClick={toggleTheme}
            aria-pressed={isDarkMode}
            title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? (
              <Sun size={18} strokeWidth={1.9} />
            ) : (
              <Moon size={18} strokeWidth={1.9} />
            )}
          </button>
          <button
            type="button"
            className="header-help-btn"
            onClick={openHelp}
            title="Help and support"
            aria-label="Help and support"
          >
            <CircleHelp size={18} strokeWidth={1.9} />
          </button>
          <span className="header-timestamp">{timestamp}</span>
          {currentUser?.full_name && (
            <HeaderMenu
              wrapClassName="header-user-menu"
              triggerClassName="header-user-trigger"
              panelClassName="header-user-dropdown"
              ariaLabel={`Account menu for ${currentUser.full_name}`}
              triggerContent={
                <>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "var(--ink-navy)",
                    border: "1.5px solid rgba(255,255,255,0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#ffffff",
                    flexShrink: 0,
                    letterSpacing: "0.03em",
                  }}
                >
                  {currentUser.full_name
                    .split(" ")
                    .map((name) => name[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div className="header-user-identity">
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-navy)", lineHeight: 1.2 }}>
                    {currentUser.full_name}
                  </span>
                  {currentUser.position && (
                    <span style={{ fontSize: 11, fontWeight: 500, color: "var(--text2)", lineHeight: 1.2 }}>
                      {currentUser.position}
                    </span>
                  )}
                </div>
                <ChevronDown
                  size={15}
                  strokeWidth={2}
                  style={{ color: "var(--text2)", flexShrink: 0, marginLeft: 2 }}
                />
                </>
              }
            >
                <button
                  className={`header-nav-dropdown-item${currentPage === "Alerts" ? " active" : ""}`}
                  onClick={() => setCurrentPage("Alerts")}
                >
                  <span
                    style={{
                      position: "relative",
                      display: "inline-flex",
                      marginRight: 9,
                      flexShrink: 0,
                    }}
                  >
                    <Bell size={15} strokeWidth={1.8} />
                    {activeAlertManagerCount > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: -3,
                          right: -3,
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: "var(--blue)",
                        }}
                      />
                    )}
                  </span>
                  Alert Manager
                </button>

                {allowedPages.has("EmailCampaigns") && (
                  <button
                    className={`header-nav-dropdown-item${currentPage === "EmailCampaigns" ? " active" : ""}`}
                    onClick={() => setCurrentPage("EmailCampaigns")}
                  >
                    <Send size={15} strokeWidth={1.8} style={{ marginRight: 9, flexShrink: 0 }} />
                    Mass Email Tool
                  </button>
                )}

                {allowedPages.has("Knowledge Base") && (
                  <button
                    className={`header-nav-dropdown-item${currentPage === "Knowledge Base" ? " active" : ""}`}
                    onClick={() => setCurrentPage("Knowledge Base")}
                  >
                    <CircleHelp size={15} strokeWidth={1.8} style={{ marginRight: 9, flexShrink: 0 }} />
                    Knowledge Base
                  </button>
                )}

                {allowedPages.has("Support Tickets") && (
                  <button
                    className={`header-nav-dropdown-item${currentPage === "Support Tickets" ? " active" : ""}`}
                    onClick={() => setCurrentPage("Support Tickets")}
                  >
                    <ClipboardCheck size={15} strokeWidth={1.8} style={{ marginRight: 9, flexShrink: 0 }} />
                    Support Tickets
                  </button>
                )}

                {allowedPages.has("System Status") && (
                  <button
                    className={`header-nav-dropdown-item${currentPage === "System Status" ? " active" : ""}`}
                    onClick={() => setCurrentPage("System Status")}
                  >
                    <Activity
                      size={15}
                      strokeWidth={1.8}
                      style={{ marginRight: 9, flexShrink: 0 }}
                    />
                    System Status
                  </button>
                )}

                {canAccessAdmin && (
                  <button
                    className={`header-nav-dropdown-item${currentPage === "Admin" ? " active" : ""}`}
                    onClick={() => setCurrentPage("Admin")}
                  >
                    <ShieldCheck
                      size={15}
                      strokeWidth={1.8}
                      style={{ marginRight: 9, flexShrink: 0 }}
                    />
                    Admin
                  </button>
                )}

                <div className="header-user-dropdown-divider" />

                <button
                  className="header-nav-dropdown-item"
                  onClick={() => setSettingsMode("password")}
                >
                  <KeyRound size={15} strokeWidth={1.8} style={{ marginRight: 9, flexShrink: 0 }} />
                  Change Password
                </button>

                <button
                  className="header-nav-dropdown-item"
                  onClick={() => setSettingsMode("email")}
                >
                  <Mail size={15} strokeWidth={1.8} style={{ marginRight: 9, flexShrink: 0 }} />
                  Change Email
                </button>

                <button
                  className="header-nav-dropdown-item"
                  onClick={() => setSettingsMode("timezone")}
                >
                  <Clock size={15} strokeWidth={1.8} style={{ marginRight: 9, flexShrink: 0 }} />
                  Timezone
                </button>

                <div className="header-user-dropdown-divider" />

                <button className="header-nav-dropdown-item" onClick={handleLogout}>
                  <LogOut size={15} strokeWidth={1.8} style={{ marginRight: 9, flexShrink: 0 }} />
                  Sign out
                </button>
            </HeaderMenu>
          )}
        </div>
      </header>

      {isPhone && (
        <MobileNav
          sectionEntries={visibleSections}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageLabel={pageLabel}
          pageIcons={PAGE_ICONS}
          alertCount={activeAlertManagerCount}
          onPrefetch={prefetchPage}
        />
      )}

      <div
        id="fox-body"
        className={[isPhone ? "has-mobile-nav" : "", isTablet ? "has-rail" : ""]
          .filter(Boolean)
          .join(" ") || undefined}
      >
        {isTablet && (
          <TabletRail
            sectionEntries={visibleSections}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageLabel={pageLabel}
            pageIcons={PAGE_ICONS}
            alertCount={activeAlertManagerCount}
            onPrefetch={prefetchPage}
            currentUser={currentUser}
          />
        )}
        <main id="fox-main">
          {currentPage === "Home" && (
            <HomeView
              allowedPages={allowedPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              currentUser={currentUser}
            />
          )}

          {currentPage === "Admin" && (
            <>
              <BackBar title="" />
              <div className="fox-view-body">
                <React.Suspense fallback={<LoadingBar active pct={null} />}>
                  <AdminPanel onBack={() => setCurrentPage("Home")} />
                </React.Suspense>
              </div>
            </>
          )}

          {pageContent && currentPage !== "Admin" && (
            <>
              {currentPage !== "Home" && currentPage !== "Admin" && (
                <BackBar
                  title={pageLabel(currentPage)}
                  freshness={freshness}
                  rightSlot={
                    <>
                      {currentPage === "Cancellations" && cancellationsExportHandler && (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => cancellationsExportHandler()}
                          title="Export to Excel"
                          aria-label="Export to Excel"
                        >
                          <Download size={16} />
                        </button>
                      )}
                      {activeAlertDashboardKey && (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => setShowDashboardAlertModal(true)}
                          title={dashboardAlertTitle}
                          aria-label={dashboardAlertTitle}
                          style={dashboardAlertButtonStyle}
                        >
                          <DashboardAlertIcon size={16} />
                          {hasActiveDashboardAlerts && (
                            <span
                              style={{
                                position: "absolute",
                                top: -4,
                                right: -4,
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: "var(--blue)",
                                color: "white",
                                fontSize: 10,
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {activeDashboardAlertCount}
                            </span>
                          )}
                        </button>
                      )}
                      {canUploadFlights && currentPage === "Departure & Arrival Analysis" && (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => setShowFlightsUpload(true)}
                          title="Upload flight data"
                          aria-label="Upload flight data"
                        >
                          <UploadCloud size={16} />
                        </button>
                      )}
                      {canManageTechEfficiencyMappings && currentPage === "Tech Efficiency" && techEfficiencyMappingsHandler && (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => techEfficiencyMappingsHandler()}
                          title="Manage efficiency mappings"
                          aria-label="Manage efficiency mappings"
                        >
                          <Settings size={16} />
                        </button>
                      )}
                      {currentPage === "Safety Culture Survey" && safetyCultureReportHandler && (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => safetyCultureReportHandler()}
                          title="Download the SRB report"
                          aria-label="Download the SRB report"
                        >
                          <Download size={16} />
                        </button>
                      )}
                      {currentPage === "Safety Culture Survey" && safetyCultureTriageHandler && (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => safetyCultureTriageHandler()}
                          title="Review comments"
                          aria-label="Review comments"
                        >
                          <MessageSquare size={16} />
                        </button>
                      )}
                      {currentPage === "Safety Culture Survey" && safetyCultureSetupHandler && (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => safetyCultureSetupHandler()}
                          title="Set up this wave"
                          aria-label="Set up this wave"
                        >
                          <Settings size={16} />
                        </button>
                      )}
                      {currentPage === "Financial Statements" && financialSettingsHandler && (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => financialSettingsHandler()}
                          title="Financial Statements settings"
                          aria-label="Financial Statements settings"
                        >
                          <Settings size={16} />
                        </button>
                      )}
                      {canUploadTechEfficiency && currentPage === "Tech Efficiency" && (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => setShowTechEfficiencyUpload(true)}
                          title="Upload Paylocity punch data"
                          aria-label="Upload Paylocity punch data"
                        >
                          <UploadCloud size={16} />
                        </button>
                      )}
                    </>
                  }
                />
              )}
              <div className="fox-view-body">
                {/* Only the page body suspends. The back bar above it carries
                    the page title and the freshness stamp, and keeping those
                    painted while the chunk arrives is the difference between
                    "Aircraft Status, loading" and a blank screen. */}
                <React.Suspense fallback={<LoadingBar active pct={null} />}>
                  {pageContent}
                </React.Suspense>
              </div>
            </>
          )}
        </main>
      </div>

      {showDashboardAlertModal && activeAlertDashboardKey && (
        <React.Suspense fallback={null}>
          <AlertModal
            dashboardKey={activeAlertDashboardKey}
            onClose={() => {
              setShowDashboardAlertModal(false);
              fetchDashboardHeaderAlerts();
              fetchActiveAlertManagerCount();
            }}
          />
        </React.Suspense>
      )}

      {/* A drawer, not a modal. Help is a place you visit while working -- it
          needs to stay open next to the screen you are asking about, keep its
          own history, and hold a long article without being trapped in a fixed
          dialog. The scrim is deliberately light so the page behind stays
          readable. */}
      {showHelp && (
        <>
          <div className="help-scrim" onClick={() => setShowHelp(false)} />
          <aside className="help-drawer" role="dialog" aria-label="Help">
            <div className="help-drawer-bar">
              <div>
                <div className="help-drawer-title">Help</div>
                <div className="help-drawer-sub">{pageLabel(currentPage)}</div>
              </div>
              <button
                className="help-drawer-close"
                onClick={() => setShowHelp(false)}
                aria-label="Close help"
              >
                <X size={17} />
              </button>
            </div>
            {/*
              Same-origin iframe, so the fox_token cookie set at sign-in
              authenticates it -- no token in a URL, and nothing to keep in
              sync with this component.

              The theme is the one exception, and it travels two ways. It is in
              the src so the panel's first paint already matches the app; it is
              NOT re-read from there afterwards, because changing src reloads
              the frame and would throw away a half-written request. The effect
              below posts later changes instead.
            */}
            <iframe
              ref={helpFrameRef}
              onLoad={postHelpTheme}
              title="FOX Help"
              src={`/kb-widget?app=fox&chrome=0&theme=${helpFrameTheme.current}&page=${encodeURIComponent(pageLabel(currentPage))}`}
              className="help-drawer-frame"
            />
          </aside>
        </>
      )}


      {showFlightsUpload && (
        <React.Suspense fallback={null}>
          <FlightsUploadModal
            onClose={() => setShowFlightsUpload(false)}
            onSuccess={() => window.location.reload()}
          />
        </React.Suspense>
      )}

      {showTechEfficiencyUpload && (
        <React.Suspense fallback={null}>
          <TechEfficiencyUploadModal
            onClose={() => setShowTechEfficiencyUpload(false)}
            onUploaded={() => setShowTechEfficiencyUpload(false)}
          />
        </React.Suspense>
      )}

      {settingsMode && (
        <UserSettingsModal
          currentUser={currentUser}
          mode={settingsMode}
          onClose={() => setSettingsMode(null)}
          onUserUpdated={fetchCurrentUser}
        />
      )}
    </div>
    </TimezoneProvider>
  );
}
